import { styled } from "@pigment-css/react";
import { Flex, SectionDivider } from "../../../primitives";
import { useTranslations, useLocale } from "@acto/web-common/locales";
import { useComparisonModeContext, useCurrencyContext } from "@acto/taskpane/core/contexts/TenantSettingsContext";
import { toGqlKpiComparisonMode } from "@acto/taskpane/core/data-access/customers/kpiTransformers";
import { StandardPeriod } from "@acto/taskpane/core/domain";
import { DonutChart } from "./DonutChart";
import { useCustomerKpiFigures } from "@acto/taskpane/core/data-access/customers/useCustomerKpiFigures";
import { CHART_COLORS } from "../../ProductDrilldownSidePanel/productDrill";
import { useProductGroups } from "@acto/taskpane/field-sales-2/data-access/useProductGroups";
import { ProductRowList, formatSignedPercent, type ProductRowView } from "../../ui";
import type { ProductGroupRef } from "../../ProductDrilldownSidePanel/productDrill";
import {
  type CustomerMetric,
  METRIC_KPI_TYPE,
  METRIC_PRODUCT_KPI,
  formatMetricWithoutRatio,
  useMetricLabels,
} from "../customerMetric";

interface CustomerProductsSectionProps {
  clientId: string;
  period?: StandardPeriod;
  dateRange?: { startDate: string; endDate: string };
  periodLabel?: string;
  showTrends: boolean;
  onProductGroupOpen: (group: ProductGroupRef, metric: CustomerMetric) => void;
  metric: CustomerMetric;
}

const SectionTitle = styled("h3")(({ theme }) => ({
  fontFamily: theme.managementView.font.family,
  fontSize: theme.managementView.font.size[16],
  fontWeight: theme.managementView.font.weight[400],
  color: theme.managementView.colors.neutral[100],
  margin: 0,
}));

const Subtitle = styled("p")(({ theme }) => ({
  fontFamily: theme.managementView.font.familySecondary,
  fontSize: theme.managementView.font.size[14],
  fontWeight: theme.managementView.font.weight[500],
  color: theme.managementView.colors.neutral[56],
  margin: 0,
}));

const ContentWrapper = styled("div")(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  gap: theme.managementView.spacing[24],
}));

const ChartWrapper = styled("div")(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  gap: theme.managementView.spacing[24],
}));

const LoadingSkeleton = styled("div")(({ theme }) => ({
  width: "100%",
  height: 300,
  backgroundColor: theme.managementView.colors.neutral[4],
  borderRadius: theme.managementView.radius[16],
  animation: "pulse 1.5s ease-in-out infinite",
  "@keyframes pulse": {
    "0%, 100%": { opacity: 1 },
    "50%": { opacity: 0.5 },
  },
}));

const EmptyState = styled("div")(({ theme }) => ({
  textAlign: "center",
  padding: theme.managementView.spacing[32],
  color: theme.managementView.colors.neutral[56],
  fontSize: theme.managementView.font.size[14],
}));

const DEFAULT_GROUP_COLOR = "#C6C6F3";
const REMAINDER_COLOR = "#E4E4E7";
const DONUT_SLICE_COUNT = 5;

export const CustomerProductsSection = ({
  clientId,
  period = StandardPeriod.LAST_12_MONTHS,
  dateRange,
  periodLabel,
  showTrends,
  onProductGroupOpen,
  metric,
}: CustomerProductsSectionProps) => {
  const t = useTranslations();
  const { locale } = useLocale();
  const currency = useCurrencyContext();
  const metricLabels = useMetricLabels();
  const tenantComparisonMode = useComparisonModeContext();
  const comparisonMode = toGqlKpiComparisonMode(tenantComparisonMode);

  const groups = useProductGroups({
    clientId,
    dateRange,
    selectedKpi: METRIC_PRODUCT_KPI[metric],
    comparisonMode,
  });

  // Order counts do not add up across groups (one order spans several), so the donut centre is
  // the customer's own total, never a sum of slices.
  const customerFigures = useCustomerKpiFigures({
    clientId,
    period,
    dateRange,
    comparisonMode: tenantComparisonMode,
  });

  const customerTotal = customerFigures.isFetching ? null : customerFigures.byKpi[METRIC_KPI_TYPE[metric]];
  const slices = groups.items.slice(0, DONUT_SLICE_COUNT).filter((item) => item.value > 0);
  const colorById = new Map(slices.map((item, index) => [item.id, CHART_COLORS[index % CHART_COLORS.length]]));
  const total = customerTotal?.totalValue ?? 0;
  // Revenue and profit add up across groups, so the customer total minus the shown slices is the
  // groups left out.
  const slicesSum = slices.reduce((sum, item) => sum + item.value, 0);
  const remainder = metric !== "orders" && customerTotal ? total - slicesSum : 0;
  const hasRemainder = remainder > 0;
  // A donut cannot draw a loss, and losses pull the customer's profit below the sum of the profitable
  // groups; measuring shares against the larger of the two keeps them from adding up past 100%.
  const shareBase = customerTotal ? Math.max(total, slicesSum) : 0;
  const donutNote =
    metric === "margin" && groups.items.some((item) => item.value < 0)
      ? t("managementView.customerDetails.lossGroupsExcluded")
      : null;

  const labels = [
    ...slices.map((item) => item.name),
    ...(hasRemainder ? [t("managementView.customerDetails.otherGroups")] : []),
  ];
  const values = [...slices.map((item) => item.value), ...(hasRemainder ? [remainder] : [])];
  const colors = [
    ...slices.map((item) => colorById.get(item.id) ?? DEFAULT_GROUP_COLOR),
    ...(hasRemainder ? [REMAINDER_COLOR] : []),
  ];
  const centerText = customerTotal ? formatMetricWithoutRatio(metric, total, locale, currency) : "";
  const centerSubtext =
    showTrends && customerTotal?.percentageChange ? formatSignedPercent(customerTotal.percentageChange) : "";
  const tooltipFormatter = (value: number, index: number) => {
    const formatted = slices[index]?.formattedValue ?? formatMetricWithoutRatio(metric, value, locale, currency);
    return metric === "orders" || shareBase <= 0
      ? [formatted]
      : [formatted, `${((value / shareBase) * 100).toFixed(1)}% ${t("kpi.ofTotal")}`];
  };
  const rows: ProductRowView[] = groups.items.map((item) => ({
    key: item.id,
    name: item.name,
    externalReference: item.externalReference,
    formattedValue: item.formattedValue ?? "-",
    change: showTrends ? item.change : null,
    color: colorById.get(item.id) ?? DEFAULT_GROUP_COLOR,
    onOpen: () =>
      onProductGroupOpen({ id: item.id, name: item.name, externalReference: item.externalReference }, metric),
  }));

  return (
    <Flex direction="column" gap={12}>
      <SectionTitle>{t("managementView.customerDetails.products")}</SectionTitle>

      <ContentWrapper>
        {groups.isPending ? (
          <LoadingSkeleton />
        ) : groups.items.length === 0 ? (
          <EmptyState>{t("managementView.customerDetails.noDataAvailable")}</EmptyState>
        ) : (
          <>
            <ChartWrapper>
              <DonutChart
                labels={labels}
                values={values}
                colors={colors}
                centerText={centerText}
                centerSubtext={centerSubtext}
                tooltipFormatter={tooltipFormatter}
              />

              <Subtitle>
                {metricLabels[metric].totalLabel} ({periodLabel})
              </Subtitle>
              {donutNote && <Subtitle>{donutNote}</Subtitle>}
            </ChartWrapper>

            <ProductRowList rows={rows} isLoadingMore={groups.isLoadingMore} />

            {groups.hasMore && !groups.isLoadingMore && <SectionDivider onClick={groups.loadMore} />}
          </>
        )}
      </ContentWrapper>
    </Flex>
  );
};
